/**
 * Mock data for Patient status detail (Operations).
 * Chart series comes from operationsMockData; replace table with API when ready.
 */

import type { RangeKey } from '@dataTypes/dashboard.types'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import { getPatientStatusData } from '@components/pages/Dashboard/Operations/operationsMockData'

export type PatientStatusDetailStatus =
  | 'Active'
  | 'Insurance Pend.'
  | 'Payment plan'
  | 'Collections'

export type PatientStatusDetailRow = {
  id: string
  patientName: string
  provider: string
  since: string
  status: PatientStatusDetailStatus
}

const STATUS_FIGMA_1D: PatientStatusDetailRow[] = [
  {
    id: '1',
    patientName: 'Sophia Martinez',
    provider: 'Dr. John Smith',
    since: 'June 21, 2026',
    status: 'Active',
  },
  {
    id: '2',
    patientName: "Liam O'Connor",
    provider: 'Dr. David Wilson',
    since: 'July 4, 2026',
    status: 'Active',
  },
  {
    id: '3',
    patientName: 'Isabella Johnson',
    provider: 'Dr. Alfred Weston',
    since: 'August 15, 2026',
    status: 'Insurance Pend.',
  },
  {
    id: '4',
    patientName: 'Ethan Chen',
    provider: 'Dr. Sarah Lee',
    since: 'September 30, 2026',
    status: 'Payment plan',
  },
  {
    id: '5',
    patientName: 'Maya Patel',
    provider: 'Dr. James Smith',
    since: 'October 12, 2026',
    status: 'Active',
  },
  {
    id: '6',
    patientName: 'Noah Williams',
    provider: 'Dr. Michael Brown',
    since: 'November 23, 2026',
    status: 'Collections',
  },
  {
    id: '7',
    patientName: 'Ava Robinson',
    provider: 'Dr. David Lee',
    since: 'December 7, 2026',
    status: 'Insurance Pend.',
  },
  {
    id: '8',
    patientName: 'Lucas Nguyen',
    provider: 'Dr. Emily Carter',
    since: 'January 18, 2026',
    status: 'Payment plan',
  },
  {
    id: '9',
    patientName: 'Emily Davis',
    provider: 'Dr. Michael Johnson',
    since: 'February 28, 2026',
    status: 'Payment plan',
  },
  {
    id: '10',
    patientName: 'Benjamin Lee',
    provider: 'Dr. Sophia Martinez',
    since: 'March 10, 2026',
    status: 'Payment plan',
  },
]

const EXTRA_NAMES = [
  'Jack F. Monroe',
  'Paula P. Tran',
  'James R. Huang',
  'Maria E. Lopez',
]

const PROVIDERS = [
  'Dr. John Smith',
  'Dr. David Wilson',
  'Dr. Alfred Weston',
  'Dr. Sarah Lee',
  'Dr. James Smith',
  'Dr. Michael Brown',
]

const TABLE_STATUSES: PatientStatusDetailStatus[] = [
  'Active',
  'Insurance Pend.',
  'Payment plan',
  'Collections',
]

const VISITS = [
  'June 21, 2026',
  'July 4, 2026',
  'August 15, 2026',
  'September 30, 2026',
  'October 12, 2026',
  'November 23, 2026',
  'December 7, 2026',
  'January 18, 2026',
  'February 28, 2026',
  'March 10, 2026',
]

function fmtCount(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n)
}

export function getPatientStatusDetailSummary(range: RangeKey): {
  totalPatients: string
  active: string
  activePct: string
  newPatients: string
} {
  const rows = getPatientStatusData(range)
  const total = rows.reduce((s, r) => s + r.value, 0)
  const active = rows.find((r) => r.label === 'Active')?.value ?? 0
  const newPatients = rows.find((r) => r.label === 'New patients')?.value ?? 0
  const activePct =
    total > 0 ? `${Math.round((active / total) * 1000) / 10}%` : '0%'
  return {
    totalPatients: fmtCount(total),
    active: fmtCount(active),
    activePct,
    newPatients: fmtCount(newPatients),
  }
}

export function getPatientStatusDetailMetrics(
  range: RangeKey,
): DashboardMetricItemData[] {
  if (range === '1D') {
    return [
      {
        id: 'ps-total',
        leftLabel: 'Total patients',
        value: '20',
      },
      { id: 'ps-active', leftLabel: 'Active', value: '12' },
      { id: 'ps-active-share', leftLabel: 'Active share', value: '60%' },
      { id: 'ps-new', leftLabel: 'New patients', value: '3' },
    ]
  }

  const s = getPatientStatusDetailSummary(range)
  return [
    {
      id: 'ps-total',
      leftLabel: 'Total patients',
      value: s.totalPatients,
    },
    { id: 'ps-active', leftLabel: 'Active', value: s.active },
    {
      id: 'ps-active-share',
      leftLabel: 'Active share',
      value: s.activePct,
    },
    {
      id: 'ps-new',
      leftLabel: 'New patients',
      value: s.newPatients,
    },
  ]
}

export function getPatientStatusDetailTableData(
  range: RangeKey,
): PatientStatusDetailRow[] {
  if (range === '1D') return STATUS_FIGMA_1D

  return Array.from({ length: 10 }, (_, i) => {
    const base = STATUS_FIGMA_1D[i % STATUS_FIGMA_1D.length]
    return {
      id: `status-${range}-${i}`,
      patientName: EXTRA_NAMES[i % EXTRA_NAMES.length] ?? base.patientName,
      provider: PROVIDERS[i % PROVIDERS.length],
      since: VISITS[i % VISITS.length],
      status: TABLE_STATUSES[i % TABLE_STATUSES.length],
    }
  })
}
